// src/components/modals/PendingOrdersManualGuiaModal.tsx
// Modal para agregar guía manualmente

import React, { useState } from 'react'
import axiosInstance from '../../api/axiosInstance'
import axios from 'axios'
import toast from 'react-hot-toast'
import type { OrderDetail } from '../../types/pendingOrdersFormTypes'
import { Input, Select, Button } from '../ui'
import { FormField } from '../ui/Form'
import { XMarkIcon, TruckIcon, TagIcon, CheckIcon, CubeIcon } from '@heroicons/react/24/outline'

const ENVIA_COLVANES_VALUE = 'envía'

const isUnidadesEmpaqueValidas = (value: string): boolean => {
  if (!/^\d+$/.test(value)) return false
  const unidades = Number(value)
  return Number.isInteger(unidades) && unidades >= 1
}

interface PendingOrdersManualGuiaModalProps {
  isOpen: boolean
  orderDetail?: OrderDetail | null
  selectedTransportadora: string
  numeroGuia: string
  updatingGuia: boolean
  transportadora: Array<{ value: string; label: string }>
  onClose: () => void
  onTransportadoraChange: (value: string) => void
  onNumeroGuiaChange: (value: string) => void
  onUpdatingChange: (updating: boolean) => void
}

export const PendingOrdersManualGuiaModal: React.FC<PendingOrdersManualGuiaModalProps> = ({
  isOpen,
  orderDetail,
  selectedTransportadora,
  numeroGuia,
  updatingGuia,
  transportadora,
  onClose,
  onTransportadoraChange,
  onNumeroGuiaChange,
  onUpdatingChange
}) => {
  const [unidadesEmpaque, setUnidadesEmpaque] = useState('')
  const isEnviaColvanes = selectedTransportadora === ENVIA_COLVANES_VALUE
  const unidadesValidas = isUnidadesEmpaqueValidas(unidadesEmpaque)

  const clearUnidadesEmpaque = () => {
    setUnidadesEmpaque('')
  }

  const handleClose = () => {
    clearUnidadesEmpaque()
    onClose()
  }

  const handleTransportadoraChange = (value: string) => {
    onTransportadoraChange(value)
    if (value !== ENVIA_COLVANES_VALUE) {
      clearUnidadesEmpaque()
    }
  }

  const handleManualGuia = async () => {
    if (!selectedTransportadora || !numeroGuia.trim() || (isEnviaColvanes && !unidadesValidas)) {
      toast.error('Por favor complete todos los campos', { icon: '⚠️', position: 'top-center' })
      return
    }

    try {
      onUpdatingChange(true)
      const token = localStorage.getItem('accessToken')
      if (!token) {
        toast.error('Error de autenticación', { icon: '⚠️', position: 'top-center' })
        return
      }

      // Preparar los datos para actualizar
      const updateData = {
        guia: numeroGuia.trim(),
        status: 'asignado',
        extraDetails: {
          ...orderDetail?.extraDetails,
          transportadora: selectedTransportadora,
          ...(isEnviaColvanes ? { unidades_empaque: Number(unidadesEmpaque) } : {})
        },
        old_data_order: {
          guia: orderDetail?.guia || '',
          status: orderDetail?.status || 'processing',
          customer_note: orderDetail?.extraDetails?.customer_note?.trim() || '',
          line_items: orderDetail?.lineItems || []
        }
      }

      const response = await axiosInstance.patch(`/list/${orderDetail?.id}`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      console.log('✅ Pedido actualizado exitosamente:', JSON.stringify(response.data, null, 2))
      toast.success('Guía agregada y pedido asignado exitosamente', { icon: '✅', position: 'top-center' })

      // Cerrar modal y limpiar campos
      clearUnidadesEmpaque()
      onClose()
      onTransportadoraChange('')
      onNumeroGuiaChange('')

      // Recargar la página para mostrar los cambios
      window.location.reload()
    } catch (error) {
      console.error('❌ Error al actualizar el pedido:', error)
      if (axios.isAxiosError(error)) {
        console.error('📝 Detalles del error:', JSON.stringify({
          status: error.response?.status,
          data: error.response?.data,
          headers: error.response?.headers
        }, null, 2))
      }
      toast.error('Error al agregar la guía', { icon: '⚠️', position: 'top-center' })
    } finally {
      onUpdatingChange(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className="relative bg-white rounded-lg shadow-xl p-8 m-4 max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h3>Agregar Guía Manual</h3>
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            className="p-1"
          >
            <XMarkIcon className="h-6 w-6" />
          </Button>
        </div>

        <div className="space-y-6">
          <FormField label="Transportadora" required icon={TruckIcon}>
            <Select
              value={selectedTransportadora}
              onChange={(e) => handleTransportadoraChange(e.target.value)}
              options={[
                { value: '', label: 'Seleccione una transportadora' },
                ...transportadora.filter(trans => trans.value !== '').map((trans) => ({
                  value: trans.value,
                  label: trans.label
                }))
              ]}
              required
            />
          </FormField>

          <FormField label="Número de Guía" required icon={TagIcon}>
            <Input
              type="text"
              value={numeroGuia}
              onChange={(e) => onNumeroGuiaChange(e.target.value)}
              placeholder="Ingrese el número de guía"
              required
            />
          </FormField>

          {isEnviaColvanes && (
            <FormField label="Unidades de empaque" required icon={CubeIcon}>
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                value={unidadesEmpaque}
                onChange={(e) => {
                  const next = e.target.value
                  if (next === '' || /^\d+$/.test(next)) {
                    setUnidadesEmpaque(next)
                  }
                }}
                placeholder="Ingrese las unidades de empaque"
                aria-label="Unidades de empaque"
                required
              />
            </FormField>
          )}

          <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              leftIcon={XMarkIcon}
              fullWidth
              className="sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="success"
              onClick={handleManualGuia}
              disabled={updatingGuia || !selectedTransportadora || !numeroGuia.trim() || (isEnviaColvanes && !unidadesValidas)}
              isLoading={updatingGuia}
              leftIcon={CheckIcon}
              fullWidth
              className="sm:w-auto"
            >
              {updatingGuia ? 'Actualizando...' : 'Agregar Guía'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

